<script setup>
    import Calendar from 'primevue/calendar';
    import Chart from 'primevue/chart';
    import { ref, defineProps, onMounted, onUnmounted, watch } from 'vue';
    import Button from 'primevue/button';
    import DataTable from 'primevue/datatable';
    import Column from 'primevue/column';
    import api from '../lib/serverApi';
    import utils from '../lib/utils';
    import dialogs from '../lib/dialogs';


    const interval = ref({
        start: utils.roundTimeToDay(new Date()),
        end: utils.incTimeDays(utils.roundTimeToDay(new Date()), 1),
    });

    const props = defineProps(['entitieId', 'maximized']);
    let entitieConfig = null;
    let historyValues= null;
    const viewMode = ref('TABLE');
    const loading = ref(false);
    const tableHeight = ref('50vh');
    const graphHeight = ref(window.innerHeight * 0.587);

    const documentStyle = getComputedStyle(document.documentElement);
    const chartData = ref({
        labels: [],
        datasets: [
            {
                label: 'Значения',
                data: [],
                fill: false,
                borderColor: documentStyle.getPropertyValue('--blue-500'),
                tension: 0.4
            },
        ]
    })

    const chartOptions = ref({
        maintainAspectRatio: false,
        aspectRatio: 0.6,
        plugins: {
            legend: {
                display: false
            }
        },
        animation: {
            duration: 0
        },
        scales: {
            x: {
                ticks: {
                    color: documentStyle.getPropertyValue('--text-color'),
                },
                grid: {
                    color: documentStyle.getPropertyValue('--text-color-secondary'),
                }
            },
            y: {
                ticks: {
                    color: documentStyle.getPropertyValue('--text-color'),
                },
                grid: {
                    color: documentStyle.getPropertyValue('--text-color-secondary'),
                }
            }
        }
    });

    onMounted(() => {
        api.getEntitiesConfig([props.entitieId])
        .then(data => entitieConfig = data[0])
        .catch(err => dialogs.showError(err.message));
        window.addEventListener('resize', onResizeForm);
        onResizeForm();
    });

    onUnmounted(() => {
        window.removeEventListener('resize', onResizeForm);
    })

    function onResizeForm() {
        if (!props.maximized) return;
        let height = document.getElementById('historyDataViewPanel').clientHeight -
            document.getElementById('historyDataViewPanelHeader').offsetHeight - 55;
        tableHeight.value = `${height}px`;

        graphHeight.value = window.innerHeight -
            document.getElementById('historyDataViewPanelHeader').offsetHeight - 55;
        if (viewMode.value === 'GRAPH') {
            viewMode.value = '';
            setTimeout(() => viewMode.value = 'GRAPH', 1);
        }
    }

    watch(props, () => {
        if (!props.maximized) {
            tableHeight.value = '50vh';
            graphHeight.value = window.innerHeight * 0.587;
            if (viewMode.value === 'GRAPH') {
                viewMode.value = '';
                setTimeout(() => viewMode.value = 'GRAPH', 1);
            }
        } else setTimeout(() => onResizeForm(), 1);
    });

    function onRefresh() {
        if (!entitieConfig) return;

        loading.value = true;
        api.getEntitieHistoryValues(props.entitieId, interval.value.start, interval.value.end)
        .then(data => {
            historyValues = data.map(item => {
                const res = {
                    moment: utils.dateToString(item.moment),
                    value: item.value,
                    status: item.status
                }
                return res;
            });

            chartData.value.labels = data.map(item => utils.dateToString(item.moment))
            chartData.value.datasets[0].data = data.map(item => item.value);

            loading.value = false;
        })
        .catch(err => dialogs.showException(err));
    }
</script>

<template>
    <div id="historyDataViewPanel" class="h-full w-full">
        <div id="historyDataViewPanelHeader" class="toolHeader">
            <span class="mr-2">Интервал, от</span>
            <Calendar class="mr-2 w-12rem" v-model="interval.start" showIcon />
            <span class="mr-2">до</span>
            <Calendar class="mr-2 w-12rem" v-model="interval.end" showIcon />
            <Button icon="pi pi-refresh" label="Обновить" @click="onRefresh" />
            <div class="headerButtons">
                <Button class="mr-2" icon="pi pi-table" label="Таблица" outlined @click="viewMode='TABLE'" />
                <Button icon="pi pi-chart-line" label="График" outlined @click="viewMode='GRAPH'" />
            </div>
        </div>
        <div v-if="viewMode === 'TABLE'">
            <DataTable
                showGridlines
                :value="historyValues"
                :class="`p-datatable-sm`"
                :rows="50"
                :rowsPerPageOptions="[20, 50, 100, 1000]"
                :paginator="true"
                stripedRows
                :loading="loading"
                scrollable
                :scrollHeight="tableHeight"
            >
                <template #empty>Нет данны для отображения.</template>
                <template #loading>Загрузка данных. Пожалуйста подождите.</template>
                <Column field="moment" header="Дата и время"></Column>
                <Column field="value" header="Ззначение"></Column>
                <Column field="status" header="Статаус"></Column>
            </DataTable>
        </div>

        <div v-if="viewMode === 'GRAPH'">
            <Chart type="line" :data="chartData" :options="chartOptions" :height="graphHeight" />
        </div>
    </div>
</template>

<style scoped>
.toolHeader {
    display: flex;
    align-items: center;
    margin: 0;
    padding: 0.6rem 0 0.6rem 0;;
}
</style>
